import React from "react";
import CircularProgress from "@mui/material/CircularProgress";

/**
 * Link card component for displaying repository links
 * @param {Object} props
 * @param {Object} props.link - Link data object with Name, Category, Link properties
 * @param {Function} props.onEdit - Callback when edit button is clicked (optional)
 * @param {Function} props.onDelete - Callback when delete button is clicked
 * @param {boolean} props.showEdit - Whether to show edit button (default: false)
 * @param {boolean} props.canDelete - Whether the delete action is permitted (default: true)
 */
const LinkCard = ({ link, onEdit, onDelete, showEdit = false, canDelete = true }) => {
  const linkUrl = link.Link || link["File Link"] || "";
  const isAnalyzed =
    link.vector_upload_status === "completed" || link.vector_upload_status === "complete";
  const isError =
    link.vector_upload_status === "error" || link.vector_upload_status === "failed";
  const isAnalyzing = !isAnalyzed && !isError && !!link.vector_upload_status;
  // null/undefined/unknown status — backend hasn't set a state yet
  const isPending = !isAnalyzed && !isError && !link.vector_upload_status;

  const statusBadge = isPending ? (
    <span
      style={{
        display: "inline-block",
        backgroundColor: "#f5f5f5",
        color: "#666",
        padding: "2px 10px",
        borderRadius: "5px",
        fontSize: "12px",
        whiteSpace: "nowrap",
      }}
    >
      Pending
    </span>
  ) : isAnalyzing ? (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        backgroundColor: "#fff3e0",
        color: "#e65100",
        padding: "2px 10px",
        borderRadius: "5px",
        fontSize: "12px",
        whiteSpace: "nowrap",
      }}
    >
      <CircularProgress size={12} thickness={5} sx={{ color: "#666" }} />
      Processing
    </span>
  ) : isError ? (
    <span
      style={{
        display: "inline-block",
        backgroundColor: "#ffebee",
        color: "#c62828",
        padding: "2px 10px",
        borderRadius: "5px",
        fontSize: "12px",
        whiteSpace: "nowrap",
      }}
    >
      Error
    </span>
  ) : (
    <span
      style={{
        display: "inline-block",
        backgroundColor: "#407775",
        color: "#fff",
        padding: "2px 10px",
        borderRadius: "5px",
        fontSize: "12px",
        whiteSpace: "nowrap",
      }}
    >
      Processed
    </span>
  );

  return (
    <div className="link-card">
      <div className="link-header">
        <span className="category">{link.Category}</span>
        <h3 className="link-title">{link.Name}</h3>
        {linkUrl ? (
          <a
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="link-url"
          >
            {linkUrl}
          </a>
        ) : (
          <span className="link-url">No link available</span>
        )}
      </div>
      <div className="action-icons" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {statusBadge}
        {showEdit && onEdit && (
          <button
            type="button"
            className="icon-btn"
            aria-label="Edit link"
            onClick={() => onEdit(link)}
          >
            <img
              src="./assets/img/reports/edit.png"
              alt="Edit Icon"
              className="edit-icon actions-icon"
            />
          </button>
        )}
        <button
          type="button"
          className="icon-btn"
          aria-label="Delete link"
          disabled={!canDelete}
          title={!canDelete ? "You don't have permission to delete" : undefined}
          onClick={() => canDelete && onDelete(link)}
        >
          <img
            src="./assets/img/reports/delete-repo.svg"
            alt="Delete Icon"
            className="delete-icon actions-icon"
          />
        </button>
      </div>
    </div>
  );
};

export default LinkCard;
